import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button,
    TextInput,
    Textarea,
    ActionIcon,
} from '@mantine/core';
import {
    IconUsers,
    IconUsersGroup,
    IconTrash,
    IconExternalLink,
    IconClipboardList,
    IconSearch,
    IconCheck,
    IconX,
    IconAlertCircle,
    IconInfoCircle,
} from '@tabler/icons-react';
import { ReservationPaxGroup, Person, Reservation } from '@turizi/shared';
import CardModal from '../card-modal';
import { PERSONS_PATH } from '@/lib/navigation';
import { useBulkCreatePersons, useReservation } from '@/hooks/api-hooks';
import CreateOrSelectPerson from '../create-or-select-person-component';
import { ShowPaxGroup, ShowPerson } from '../snippets';
import { notify } from '@/lib/notify';

interface PaxGroupModalProps {
    opened: boolean;
    onClose: () => void;
    reservation: Reservation;
    group?: ReservationPaxGroup | null;
    onSave: (data: Partial<ReservationPaxGroup>) => Promise<void>;
    onDelete?: (groupId: number) => Promise<void>;
    isLoading?: boolean;
}

export type HeadcountCategory = 'adults' | 'children' | 'seniors' | 'guests' | 'infants';

export default function PaxGroupModal({
    opened,
    onClose,
    reservation,
    group,
    onSave,
    onDelete,
    isLoading = false,
}: PaxGroupModalProps) {
    const { t } = useTranslation();
    const {
        data: resData
    } = useReservation(reservation.id);
    // const {
    //     data: allPaxGroups = []
    // } = useReservationPaxGroups(reservationId);

    const [paxsList, setPaxsList] = useState<Person[]>([]);
    const [name, setName] = useState('');

    // Bulk paste state
    const [showBulkPaste, setShowBulkPaste] = useState(false);
    const [bulkRawText, setBulkRawText] = useState('');
    const [bulkMessages, setBulkMessages] = useState<Array<{
        row?: number;
        type: 'error' | 'warning' | 'info';
        code: string;
        params?: Record<string, any>;
        line?: string;
    }>>([]);
    const [paxListSearch, setPaxListSearch] = useState('');

    const bulkCreatePersonsMutation = useBulkCreatePersons();

    const isEdit = Boolean(group?.id);

    const defaultGroupName = '';

    // Populate when opening
    useEffect(() => {
        if (opened) {
            setShowBulkPaste(false);
            setBulkRawText('');
            setBulkMessages([]);
            setPaxListSearch('');
            if (group) {
                const initialItems: Person[] = group.persons?.map(p => p) || [];
                setPaxsList(initialItems);
                setName(group.name || '');
            } else {
                setPaxsList([]);
                setName('');
            }
        }
    }, [opened, group, resData?.data?.persons]);

    const headcountTotals = useMemo(() => {
        let sumAdults = 0;
        let sumChildren = 0;
        let sumInfants = 0;
        let sumSeniors = 0;
        let sumGuests = 0;

        for (const p of paxsList) {
            sumAdults += Number(p.adults) || 0;
            sumChildren += Number(p.children) || 0;
            sumInfants += Number(p.infants) || 0;
            sumSeniors += Number(p.seniors) || 0;
            sumGuests += Number(p.guests) || 0;
        }

        return {
            adults: sumAdults,
            children: sumChildren,
            infants: sumInfants,
            seniors: sumSeniors,
            guests: sumGuests,
        };
    }, [paxsList]);

    const { adults, children, infants, seniors, guests } = headcountTotals;
    const seatPaxs = adults + children + seniors + guests;

    // Add person to items
    const addPersonItem = (pax: Person) => {
        if (pax.id && paxsList.some((p) => p.id === pax.id)) return;
        const nextList = [...paxsList, pax];
        setPaxsList(nextList);
    };

    const handleConfirmBulkImport = async () => {
        if (!bulkRawText.trim()) return;
        setBulkMessages([]);

        try {
            const res = await bulkCreatePersonsMutation.mutateAsync(bulkRawText);
            const createdPersons = res?.data || [];
            const existingIds = new Set(paxsList.map((p) => p.id).filter(Boolean));
            const merged = [...paxsList];
            for (const p of createdPersons) {
                if (p && p.id && !existingIds.has(p.id)) {
                    existingIds.add(p.id);
                    merged.push(p);
                }
            }
            setPaxsList(merged);

            if (Array.isArray(res?.messages) && res.messages.length > 0) {
                setBulkMessages(res.messages);
            }

            setBulkRawText('');
        } catch (err: any) {
            console.error('Error bulk creating passengers:', err);
            const backendMessages = err?.response?.data?.messages;
            const backendErrorCode = err?.response?.data?.error;
            if (Array.isArray(backendMessages) && backendMessages.length > 0) {
                setBulkMessages(backendMessages);
            } else if (typeof backendErrorCode === 'string') {
                setBulkMessages([{ type: 'error', code: backendErrorCode }]);
            } else {
                notify.error(t('error'), t('error'));
            }
        }
    };

    const filteredPaxsList = useMemo(() => {
        const q = paxListSearch.trim().toLowerCase();
        if (!q) return paxsList.map((pax, idx) => ({ pax, idx }));
        return paxsList
            .map((pax, idx) => ({ pax, idx }))
            .filter(({ pax }) =>
                (pax.name || '').toLowerCase().includes(q) ||
                (pax.document_number || '').toLowerCase().includes(q) ||
                (pax.phone || '').toLowerCase().includes(q) ||
                (pax.email || '').toLowerCase().includes(q)
            );
    }, [paxsList, paxListSearch]);

    // Remove passenger from group
    const handleRemovePax = (indexToRemove: number) => {
        const itemToRemove = paxsList[indexToRemove];
        if (!itemToRemove) return;

        const newItems = paxsList.filter((_, i) => i !== indexToRemove);
        setPaxsList(newItems);
    };

    // Submit / Save
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const resolvedName = (name || defaultGroupName).trim();

        const uniquePersonIds = Array.from(
            new Set((paxsList || []).map((p) => Number(p.id)).filter(Boolean))
        );

        const personIds = uniquePersonIds;

        const members = uniquePersonIds
            .map(pid => ({
                pax_group_id: group?.id,
                person_id: pid,
            }));

        const payload: Partial<ReservationPaxGroup> = {
            name: resolvedName,
            adults,
            children,
            infants,
            seniors,
            guests,
            person_ids: personIds,
            members,
        };

        await onSave(payload);
    };

    const hasAnySize = (seatPaxs + infants) > 0;

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="fit"
            header={
                <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                        <IconUsers size={15} className="text-slate-500" />
                        <span className="font-semibold text-xs text-slate-800">
                            {isEdit ? t('edit_pax_group') : t('add_pax_group')}
                        </span>
                        <Button
                            size="compact-xs"
                            variant="light"
                            color="gray"
                            leftSection={showBulkPaste ? <IconX size={12} /> : <IconClipboardList size={12} />}
                            onClick={() => setShowBulkPaste((prev) => !prev)}
                            className="text-slate-600 font-medium"
                        >
                            {showBulkPaste ? t('single_passenger_mode') : t('bulk_paste_passengers')}
                        </Button>
                    </div>
                </div>
            }
            footer={
                <div className="w-full flex flex-wrap items-center justify-between gap-2.5">
                    <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                        {isEdit && onDelete && group?.id && (
                            <ActionIcon
                                color="red"
                                variant="subtle"
                                size="md"
                                radius="md"
                                onClick={() => group.id && onDelete(group.id)}
                                disabled={isLoading}
                                title={t('delete')}
                            >
                                <IconTrash size={15} />
                            </ActionIcon>
                        )}

                        <TextInput
                            placeholder={t('pax_group_name')}
                            value={name}
                            variant='filled'
                            onChange={(e) => setName(e.currentTarget.value)}
                            size="xs"
                            leftSection={<IconUsersGroup size={13} className="text-slate-400" />}
                            className="font-medium"
                        />

                        {hasAnySize && (
                            <ShowPaxGroup
                                size="sm"
                                adults={adults}
                                children={children}
                                seniors={seniors}
                                infants={infants}
                                guests={guests}
                            />
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            radius="md"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="dark"
                            size="xs"
                            radius="md"
                            onClick={() => handleSubmit()}
                            loading={isLoading}
                        >
                            {t('save_group')}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-1">
                {showBulkPaste ? (
                    <div className="p-3 bg-slate-50/60 rounded-lg border border-slate-200/80 space-y-2.5">
                        <div className="text-[11px] text-slate-500">
                            {t('bulk_paste_instructions')}
                        </div>
                        <Textarea
                            value={bulkRawText}
                            onChange={(e) => {
                                setBulkRawText(e.currentTarget.value);
                                if (bulkMessages.length > 0) setBulkMessages([]);
                            }}
                            placeholder={t('bulk_paste_placeholder')}
                            minRows={4}
                            maxRows={8}
                            autosize
                            size="xs"
                            styles={{
                                input: {
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                },
                            }}
                        />

                        {bulkMessages.length > 0 && (
                            <div className="space-y-1 max-h-36 overflow-y-auto">
                                {bulkMessages.map((msg, idx) => {
                                    const isError = msg.type === 'error';
                                    const isWarning = msg.type === 'warning';
                                    return (
                                        <div
                                            key={`${msg.code}-${msg.row ?? idx}-${idx}`}
                                            className={`flex items-start gap-2 px-2.5 py-1.5 rounded-md border text-xs ${isError
                                                ? 'bg-red-50 border-red-200 text-red-700'
                                                : isWarning
                                                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                    : 'bg-blue-50 border-blue-200 text-blue-700'
                                                }`}
                                        >
                                            {isError || isWarning ? (
                                                <IconAlertCircle size={14} className="shrink-0 mt-0.5" />
                                            ) : (
                                                <IconInfoCircle size={14} className="shrink-0 mt-0.5" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium">
                                                    {String(t(msg.code, msg.params))}
                                                </div>
                                                {msg.line && (
                                                    <div className="font-mono text-[11px] opacity-80 truncate">
                                                        {msg.line}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-2">
                            <Button
                                size="compact-xs"
                                color="dark"
                                radius="md"
                                leftSection={<IconCheck size={13} />}
                                loading={bulkCreatePersonsMutation.isPending}
                                disabled={!bulkRawText.trim()}
                                onClick={handleConfirmBulkImport}
                            >
                                {t('add_passengers')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <CreateOrSelectPerson
                        onSelect={addPersonItem}
                        statusBarLeft={
                            <span className="text-[11px] font-medium text-slate-400">
                                {t('pax_list')} · {paxsList.length}
                            </span>
                        }
                        statusBarRight={
                            paxsList.length > 6 ? (
                                <TextInput
                                    size="xs"
                                    variant="unstyled"
                                    value={paxListSearch}
                                    onChange={(e) => setPaxListSearch(e.currentTarget.value)}
                                    placeholder={t('search_passengers')}
                                    leftSection={<IconSearch size={12} className="text-slate-400" />}
                                    rightSection={
                                        paxListSearch ? (
                                            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setPaxListSearch('')}>
                                                <IconX size={10} />
                                            </ActionIcon>
                                        ) : null
                                    }
                                    className="w-44 border-b border-slate-200"
                                />
                            ) : null
                        }
                    />
                )}

                <div className="space-y-1">
                    {showBulkPaste && (
                        <div className="flex items-center justify-between gap-2 min-h-[22px] px-0.5">
                            <span className="text-[11px] font-medium text-slate-400">
                                {t('pax_list')} · {paxsList.length}
                            </span>
                            {paxsList.length > 6 && (
                                <TextInput
                                    size="xs"
                                    variant="unstyled"
                                    value={paxListSearch}
                                    onChange={(e) => setPaxListSearch(e.currentTarget.value)}
                                    placeholder={t('search_passengers')}
                                    leftSection={<IconSearch size={12} className="text-slate-400" />}
                                    rightSection={
                                        paxListSearch ? (
                                            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setPaxListSearch('')}>
                                                <IconX size={10} />
                                            </ActionIcon>
                                        ) : null
                                    }
                                    className="w-44 border-b border-slate-200"
                                />
                            )}
                        </div>
                    )}

                    <div className="mb-20 border border-gray-100 rounded-md divide-y divide-gray-100 max-h-[340px] overflow-y-auto subtle-scrollbar">
                        {filteredPaxsList.map(({ pax, idx }) => (
                            <div
                                key={pax.id ? `pax-${pax.id}-${idx}` : `pax-${idx}`}
                                className="px-1.5 py-1.5 flex items-center justify-between gap-2 hover:bg-gray-100 rounded-sm transition-colors group"
                            >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="w-4 text-[10px] text-slate-400 tabular-nums text-right shrink-0 select-none">
                                        {idx + 1}
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        {pax.id ? (
                                            <Link
                                                to={`${PERSONS_PATH}/${pax.id}`}
                                                target="_blank"
                                                className="min-w-0 inline-flex items-center gap-1 group/link"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <ShowPerson
                                                    person={pax}
                                                    hideIcon
                                                    className="font-medium text-slate-800 text-xs group-hover/link:text-blue-600 group-hover/link:underline"
                                                />
                                                <IconExternalLink size={11} className="text-slate-300 opacity-0 group-hover/link:opacity-100 transition-opacity shrink-0" />
                                            </Link>
                                        ) : (
                                            <span className="font-medium text-xs text-slate-800 truncate">
                                                {pax.name}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => handleRemovePax(idx)}
                                    title={t('delete')}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                >
                                    <IconTrash size={13} />
                                </ActionIcon>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </CardModal>
    );
}
